import React, { useEffect, useRef } from 'react';
import Alert from 'components/styleguide/alert/Alert';
import Icon from 'components/styleguide/icon/Icon';
import LoadingSpinner from 'components/styleguide/loading_spinner/LoadingSpinner';
import { displayPct, effectiveScores } from './utils/curationScoring';
import { useAiScore } from './state/useAiScore';
import { useCurationReview } from './state/CurationReviewContext';
import Button from '../../../lj_shared/button/button';
import ExperienceBreakdown from './ExperienceBreakdown';
import FlagsList from './FlagsList';
import OverallScore from './OverallScore';
import OverrideBox from './OverrideBox';
import SkillsBreakdown from './SkillsBreakdown';

const styles = require('./AiScorePanel.module.scss');

const AI_ERROR_MESSAGES: Record<string, string> = {
  cv_text_unavailable: "The CV text couldn't be read.",
  timeout: 'The analysis is taking longer than expected.',
  trigger_failed: "The AI review couldn't be started.",
  unparseable_response: "The AI reply couldn't be interpreted.",
};

// The AI match: locked until the eligibility decision, then loading, error
// or loaded.
// On a permanent failure the curator can retry, reject, or approve by setting
// the scores manually through the overrides.
export default function AiScorePanel() {
  const { dispatch, experienceMax, experienceMin, state, urls } = useCurationReview();
  const { start } = useAiScore(urls, dispatch, state.aiStatus);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const wasLocked = useRef(!state.gateConfirmed);

  // Confirming eligibility unlocks this pane: trigger the scoring run once and move
  // focus to the section heading.
  useEffect(() => {
    if (!state.gateConfirmed) {
      wasLocked.current = true;
      return;
    }
    if (state.aiStatus === 'locked' && !state.submitted) start();
    if (wasLocked.current && headingRef.current) headingRef.current.focus();
    wasLocked.current = false;
    // eslint-disable-next-line
  }, [state.gateConfirmed]);

  const scores = effectiveScores(
    state.payload,
    state.skillStates,
    state.directOverrides,
    experienceMin,
    experienceMax,
  );

  if (!state.gateConfirmed) {
    return (
      <section aria-label="AI match & review" className={styles['ui-ai-panel__locked']}>
        <Icon aria-hidden name="lock" size="lg" />
        <h2 className={styles['ui-ai-panel__locked-title']}>AI match &amp; review</h2>
        <p className={styles['ui-ai-panel__copy']}>
          Complete the eligibility checks first to unlock this phase.
        </p>
      </section>
    );
  }

  return (
    <section aria-label="AI match & review" className={styles['ui-ai-panel']}>
      <h2 className={styles['ui-ai-panel__heading']} ref={headingRef} tabIndex={-1}>
        AI match &amp; review
      </h2>

      <span aria-live="polite" className={styles['ui-ai-panel__live']}>
        {state.aiStatus === 'loaded' &&
          `Skills ${displayPct(scores.skills)}, experience ${displayPct(
            scores.experience,
          )}, overall ${displayPct(scores.overall)}${
            scores.category === 'qualified' ? ', qualified candidate' : ''
          }`}
      </span>

      {state.aiStatus === 'loading' && (
        <div className={`${styles['ui-ai-panel__card']} ${styles['ui-ai-panel__loading']}`}>
          <LoadingSpinner context="talent" label="Analyzing" size="medium-large" />
          <p className={styles['ui-ai-panel__copy']}>
            Analyzing the CV against the job requirements. Usually under a minute.
          </p>
        </div>
      )}

      {state.aiStatus === 'error' && (
        <div className={`${styles['ui-ai-panel__card']} ${styles['ui-ai-panel__error']}`}>
          <Alert
            description={
              (state.aiError && AI_ERROR_MESSAGES[state.aiError]) ||
              'You can try again, set the scores manually below, or reject the application.'
            }
            message="The match score couldn't be computed."
            variant="error"
          />
          <Button buttonSize="xSmall" buttonType="border" isButton onClick={start} type="button">
            Try again
          </Button>
          <OverrideBox
            description="No AI analysis is available. Set the scores manually to approve."
            fields={[
              { key: 'skills_pct', label: 'Skills %' },
              { key: 'years_pct', label: 'Years score %' },
              { key: 'context_pct', label: 'Context relevance %' },
            ]}
            footnote="Experience % = (Years × 0.70) + (Context × 0.30) · Overall % = (Skills × 0.65) + (Experience × 0.35)"
            title="Manual scores"
          />
        </div>
      )}

      {state.aiStatus === 'loaded' && (
        <>
          <div className={styles['ui-ai-panel__card']}>
            <OverallScore scores={scores} />
          </div>
          <div className={styles['ui-ai-panel__card']}>
            <h3 className={styles['ui-ai-panel__card-title']}>Flags</h3>
            <FlagsList flags={state.payload ? state.payload.flags : []} />
          </div>
          <div className={styles['ui-ai-panel__card']}>
            <SkillsBreakdown scores={scores} />
          </div>
          <div className={styles['ui-ai-panel__card']}>
            <ExperienceBreakdown scores={scores} />
          </div>
        </>
      )}
    </section>
  );
}
